import React, { useEffect, useState, useMemo } from "react";
import {
  TableContainer,
  Container,
  Grid,
  Box,
  Paper,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  DataGrid,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  isLeaf,
  gridColumnLookupSelector,
  useGridSelector,
  GridColumnsPanel,
} from "@mui/x-data-grid";
import WarningIcon from "@mui/icons-material/Warning";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import useSWR from "swr";
import ProductionStatusDetails from "./ProductionStatusDetails";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

const EpisodeGrid = () => {
  const [threshold, setThreshold] = useState(0.7);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [episodesPerTab, setEpisodesPerTab] = useState(10);
  const [currentTab, setCurrentTab] = useState(0);

  const { data: productions, error: productionsError } = useSWR("/productions");
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id
      ? `/production-category-item/of-production/${selectedProduction.id}`
      : null
  );

  const { data: itemsDetails, error: detailsError } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      const token = localStorage.getItem("token");

      try {
        const itemRequests = categoryItems.map((item) =>
          fetch(
            `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/expense/test/${selectedProduction.id}/${item.id}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          )
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Error fetching item ${item.id}: ${res.statusText}`
                );
              }
              return res.json();
            })
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );

  useEffect(() => {
    const storedThreshold = localStorage.getItem("threshold");
    if (storedThreshold) {
      setThreshold(parseFloat(storedThreshold));
    }
  }, []);

  useEffect(() => {
    const savedProduction = localStorage.getItem("selectedProduction");
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find((p) => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  // Save selected production to localStorage whenever it changes
  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem(
        "selectedProduction",
        JSON.stringify(selectedProduction)
      );
    }
  }, [selectedProduction]);

  const handleCellClick = (params) => {
    // Don't handle clicks on totals row or totals column
    if (params.row.isTotal || params.field === "rowTotal") {
      return;
    }

    const episodeData = params.value;
    if (!episodeData) {
      return;
    }
    if (episodeData.amount === null) {
      return;
    }

    // Extract item ID from field name (e.g., "item-123" -> 123)
    const itemIdMatch = params.field.match(/item-(\d+)/);
    if (!itemIdMatch) return;

    const itemId = parseInt(itemIdMatch[1]);
    const episodeNum = params.row.episode;
    const itemName = params.row[`item-${itemId}-name`];
    const budget = params.row[`item-${itemId}-budget`];

    setSelectedItem(itemId);
    setSelectedColumn(episodeNum);
    setSelectedItemName(itemName);
    setSelectedBudget(budget);
  };

  const handlePanelClose = () => {
    setSelectedItem(null);
    setSelectedColumn(null);
    setSelectedItemName(null);
  };

  const handleProductionChange = (event) => {
    setSelectedProduction(productions.find((p) => p.id === event.target.value));
    setThreshold(0.7);
    setCurrentTab(0);
    localStorage.removeItem("threshold");
  };

  const handleThresholdChange = (_, newValue) => {
    setThreshold(newValue);
    localStorage.setItem("threshold", newValue.toString());
  };

  const { transposedRows, transposedColumns, episodeTabs } = useMemo(() => {
    if (!selectedProduction || !categoryItems || !itemsDetails)
      return { transposedColumns: [], transposedRows: [], episodeTabs: [] };

    const maxEpisodes = selectedProduction.no_of_episodes;
    const totalTabs = Math.ceil(maxEpisodes / episodesPerTab);
    const startEpisode = currentTab * episodesPerTab + 1;
    const endEpisode = Math.min((currentTab + 1) * episodesPerTab, maxEpisodes);
    const visibleEpisodes = Array.from({ length: endEpisode - startEpisode + 1 }, (_, i) => startEpisode + i);
    
    const tabs = Array.from({ length: totalTabs }, (_, i) => {
      const tabStart = i * episodesPerTab + 1;
      const tabEnd = Math.min((i + 1) * episodesPerTab, maxEpisodes);
      return {
        label: `Επεισόδια ${tabStart}-${tabEnd}`,
        start: tabStart,
        end: tabEnd,
        episodes: Array.from({ length: tabEnd - tabStart + 1 }, (_, j) => tabStart + j),
      };
    });

    // First, build the original data structure
    const gridRows = categoryItems.map((item) => {
      const itemDetailsList = itemsDetails
        .flat()
        .filter((detail) => detail.pci_id === item.id);

      const episodeData = Object.fromEntries(
        Array.from({ length: maxEpisodes }, (_, i) => [
          `episode-${i + 1}`,
          { amount: null, expPred: false, maxEpAm: 0 },
        ])
      );

      let budget = "";

      const categoryItem = categoryItems.find(
        (category) => category.id === item.id
      );

      if (categoryItem) {
        budget = categoryItem.pci_amount;
      }

      itemDetailsList.forEach((itemDetail) => {
        itemDetail.exp_assigned_episodes.forEach((assignedEpisode) => {
          const episodeKey = `episode-${assignedEpisode}`;

          if (itemDetail.exp_pred === true) {
            episodeData[episodeKey].expPred = true;
          }

          const expenses = itemDetail.expense_details.filter(
            (expense) => expense.exp_d_episode === assignedEpisode
          );

          const totalExpense = expenses.reduce(
            (sum, expense) => sum + (expense.exp_d_amount || 0),
            0
          );

          episodeData[episodeKey].amount += totalExpense;

          if (itemDetail.exp_assigned_episodes?.length === maxEpisodes) {
            episodeData[episodeKey].maxEpAm += totalExpense;
          }
        });
      });

      const totalCost = Object.values(episodeData).reduce(
        (sum, ep) => sum + (ep.amount || 0),
        0
      );
      const validEpisodeCount = Object.values(episodeData).filter(
        (ep) => ep.amount !== null
      ).length;
      const averageCost =
        validEpisodeCount > 0 ? totalCost / validEpisodeCount : 0;

      return {
        item_db_id: item.id,
        id: item.id,
        item: item.pci_descr,
        budget,
        budgetTotal: budget * maxEpisodes,
        average: averageCost,
        totalItemActualCost: totalCost,
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            key,
            {
              amount: value.amount,
              exp_pred: value.expPred,
              maxEpAm: value.maxEpAm,
            },
          ])
        ),
      };
    });

    // Transpose: Episodes as rows, Items as columns (only for visible episodes in current tab)
    const transposedRowsData = visibleEpisodes.map((episodeNum) => {
      const rowData = {
        id: `episode-${episodeNum}`,
        episode: episodeNum,
        isTotal: false,
      };

      let rowTotal = 0;

      // Add data for each item as a column
      gridRows.forEach((itemRow) => {
        const episodeKey = `episode-${episodeNum}`;
        const episodeData = itemRow[episodeKey];
        rowData[`item-${itemRow.item_db_id}`] = episodeData || { amount: null, exp_pred: false };
        rowData[`item-${itemRow.item_db_id}-budget`] = itemRow.budget;
        rowData[`item-${itemRow.item_db_id}-name`] = itemRow.item;
        
        // Calculate row total
        if (episodeData && episodeData.amount !== null) {
          rowTotal += episodeData.amount;
        }
      });

      rowData.rowTotal = rowTotal;
      return rowData;
    });

    // Calculate column totals (sum of visible episodes for each item in current tab)
    const columnTotals = {};
    gridRows.forEach((itemRow) => {
      let columnTotal = 0;
      visibleEpisodes.forEach((episodeNum) => {
        const episodeKey = `episode-${episodeNum}`;
        const episodeData = itemRow[episodeKey];
        if (episodeData && episodeData.amount !== null) {
          columnTotal += episodeData.amount;
        }
      });
      columnTotals[itemRow.item_db_id] = columnTotal;
    });

    // Calculate grand total (sum of all row totals in current tab)
    const grandTotal = transposedRowsData.reduce((sum, row) => sum + (row.rowTotal || 0), 0);

    // Add totals row at the bottom
    const totalsRow = {
      id: "totals-row",
      episode: "Σύνολο",
      isTotal: true,
    };

    gridRows.forEach((itemRow) => {
      totalsRow[`item-${itemRow.item_db_id}`] = { amount: columnTotals[itemRow.item_db_id] || 0, exp_pred: false };
      totalsRow[`item-${itemRow.item_db_id}-budget`] = itemRow.budget;
      totalsRow[`item-${itemRow.item_db_id}-name`] = itemRow.item;
    });

    totalsRow.rowTotal = grandTotal;

    const allRowsWithTotals = [...transposedRowsData, totalsRow];

    // Create columns: Episode number + one column per item + totals column
    const transposedCols = [
      {
        field: "episode",
        headerName: "Επεισόδιο",
        width: 120,
        pinned: "left",
        headerAlign: "center",
        align: "center",
        sortComparator: (v1, v2, param1, param2) => {
          // Always put totals row at the end
          if (param1?.row?.isTotal) return 1;
          if (param2?.row?.isTotal) return -1;
          // For numeric episodes, sort normally
          if (typeof v1 === "number" && typeof v2 === "number") {
            return v1 - v2;
          }
          // For "Σύνολο" string, it should be at the end
          if (v1 === "Σύνολο") return 1;
          if (v2 === "Σύνολο") return -1;
          // Handle undefined/null cases
          if (v1 == null) return 1;
          if (v2 == null) return -1;
          return 0;
        },
        renderCell: (params) => (
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: params.row.isTotal ? "bold" : "bold",
              backgroundColor: params.row.isTotal ? "#f5f5f5" : "transparent",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      ...categoryItems.map((item) => {
        const itemRow = gridRows.find((r) => r.item_db_id === item.id);
        return {
          field: `item-${item.id}`,
          headerName: item.pci_descr,
          width: 150,
          headerAlign: "center",
          sortComparator: (v1, v2) => {
            const a = v1?.amount || 0;
            const b = v2?.amount || 0;
            return a - b;
          },
          renderCell: (params) => {
            const episodeData = params.value;
            const isTotalRow = params.row.isTotal;

            if (isTotalRow) {
              // Totals row - show column total
              const totalAmount = episodeData?.amount || 0;
              const formattedAmount = new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(totalAmount);

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "100%",
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    padding: "4px",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "#1976d2",
                    }}
                  >
                    {formattedAmount}
                  </Typography>
                </Box>
              );
            }

            if (!episodeData || episodeData.amount === null) return "-";

            const { amount, exp_pred } = episodeData;
            const budget = parseFloat(params.row[`item-${item.id}-budget`]) || 0;
            const isAboveBudget = amount > budget;
            const isBelowThreshold = !isAboveBudget && amount < budget * threshold;

            const formattedAmount = new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(amount);

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                  width: "100%",
                  position: "relative",
                }}
              >
                {exp_pred && (
                  <OnlinePredictionIcon
                    sx={{ color: "green", fontSize: 14 }}
                    title="Πρόβλεψη"
                  />
                )}
                {amount !== null && isBelowThreshold && !isAboveBudget && (
                  <WarningIcon sx={{ color: "orange", fontSize: 16 }} />
                )}
                <Typography
                  sx={{
                    color: isAboveBudget
                      ? "red"
                      : isBelowThreshold
                        ? "orange"
                        : "inherit",
                    fontWeight: isAboveBudget || isBelowThreshold ? "bold" : "normal",
                  }}
                >
                  {formattedAmount}
                </Typography>
              </Box>
            );
          },
        };
      }),
      {
        field: "rowTotal",
        headerName: "Σύνολο",
        width: 150,
        pinned: "right",
        headerAlign: "center",
        align: "right",
        renderCell: (params) => {
          const total = params.value || 0;
          const formattedAmount = new Intl.NumberFormat("en-IE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(total);

          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: params.row.isTotal ? "bold" : "bold",
                color: params.row.isTotal ? "#1976d2" : "#2bc12b",
                backgroundColor: params.row.isTotal ? "#f5f5f5" : "transparent",
                padding: params.row.isTotal ? "4px" : "0",
                width: "100%",
                textAlign: "right",
              }}
            >
              {formattedAmount}
            </Typography>
          );
        },
      },
    ];

    return {
      transposedRows: allRowsWithTotals,
      transposedColumns: transposedCols,
      episodeTabs: tabs,
    };
  }, [selectedProduction, categoryItems, itemsDetails, threshold, currentTab, episodesPerTab]);

  const isLoading = selectedProduction && (!categoryItems || !itemsDetails);

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  const getColumnGroupLeaves = (group, callback) => {
    group.children.forEach((child) => {
      if (isLeaf(child)) {
        callback(child.field);
      } else {
        getColumnGroupLeaves(child, callback);
      }
    });
  };

  function ColumnGroup({ group, columnLookup, apiRef, columnVisibilityModel }) {
    const leaves = React.useMemo(() => {
      const fields = [];
      getColumnGroupLeaves(group, (field) => fields.push(field));
      return fields;
    }, [group]);

    const { isGroupChecked, isGroupIndeterminate } = React.useMemo(() => {
      return {
        isGroupChecked: leaves.every(
          (field) => columnVisibilityModel[field] !== false
        ),
        isGroupIndeterminate:
          leaves.some((field) => columnVisibilityModel[field] === false) &&
          !leaves.every((field) => columnVisibilityModel[field] === false),
      };
    }, [columnVisibilityModel, leaves]);

    const toggleColumnGroup = (checked) => {
      const newColumnVisibilityModel = {
        ...columnVisibilityModel,
      };
      getColumnGroupLeaves(group, (field) => {
        newColumnVisibilityModel[field] = checked;
      });
      apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
    };

    const toggleColumn = (field, checked) => {
      apiRef.current.setColumnVisibility(field, checked);
    };

    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={isGroupChecked}
              indeterminate={isGroupIndeterminate}
              size="small"
              sx={{ p: 1 }}
            />
          }
          label={group.headerName ?? group.groupId}
          onChange={(_, newValue) => toggleColumnGroup(newValue)}
        />
        <Box sx={{ pl: 3.5 }}>
          {group.children.map((child) => {
            return isLeaf(child) ? (
              <Stack direction="row" key={child.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={columnVisibilityModel[child.field] !== false}
                      size="small"
                      sx={{ p: 1 }}
                    />
                  }
                  label={columnLookup[child.field].headerName ?? child.field}
                  onChange={(_, newValue) =>
                    toggleColumn(child.field, newValue)
                  }
                />
              </Stack>
            ) : (
              <ColumnGroup
                group={child}
                columnLookup={columnLookup}
                key={child.groupId}
                apiRef={apiRef}
                columnVisibilityModel={columnVisibilityModel}
              />
            );
          })}
        </Box>
      </div>
    );
  }

  function ColumnsPanel(props) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
    const columnVisibilityModel = useGridSelector(
      apiRef,
      gridColumnVisibilityModelSelector
    );

    const columnGroupingModel = rootProps.columnGroupingModel;

    if (!columnGroupingModel) {
      return <GridColumnsPanel {...props} />;
    }

    return (
      <Box sx={{ px: 2, py: 0.5 }}>
        {columnGroupingModel.map((group) => (
          <ColumnGroup
            group={group}
            columnLookup={columnLookup}
            columnVisibilityModel={columnVisibilityModel}
            key={group.groupId}
            apiRef={apiRef}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Category Items"
          module="Productions"
          subtitle="List of category items"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>
          <Box m="8px 0 0 0" height="80vh">
            <Paper style={{ padding: "1rem", height: "82vh", width: "100%" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="prod-label">Παραγωγή</InputLabel>
                <Select
                  labelId="prod-label"
                  id="prod-id"
                  label="Production"
                  fullWidth
                  size="small"
                  maxWidth={"100%"}
                  value={selectedProduction?.id || ""}
                  onChange={handleProductionChange}
                  style={{ width: "20%", marginBottom: "16px" }}
                >
                  {productions?.map((production) => (
                    <MenuItem key={production.id} value={production.id}>
                      {production.p_descr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" alignItems="center" mb={2} justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <Typography>Όριο:</Typography>
                  <Slider
                    value={threshold}
                    min={0.1}
                    max={1}
                    step={0.05}
                    onChange={handleThresholdChange}
                    valueLabelDisplay="auto"
                    style={{ marginLeft: "16px", width: "200px" }}
                  />
                  <Typography>{Math.round(threshold * 100)}%</Typography>
                </Box>
                {selectedProduction && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2">
                      Επεισόδια ανά καρτέλα:
                    </Typography>
                    <Select
                      value={episodesPerTab}
                      onChange={(e) => {
                        setEpisodesPerTab(e.target.value);
                        setCurrentTab(0);
                      }}
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                    </Select>
                  </Box>
                )}
              </Box>

              {selectedProduction && episodeTabs.length > 0 && (
                <Box mb={2}>
                  <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                  >
                    {episodeTabs.map((tab, index) => (
                      <Tab key={index} label={tab.label} />
                    ))}
                  </Tabs>
                </Box>
              )}

              {/* Show loading indicator only after a production is selected */}
              {isLoading && <LinearProgress />}

              {/* Transposed View: Episodes as Rows, Items as Columns */}
              {selectedProduction && categoryItems && !isLoading && transposedRows.length > 0 && (
                <Box
                  sx={{
                    position: "relative",
                    height: "66vh",
                    overflow: "hidden",
                  }}
                >
                  <DataGrid
                    sx={{
                      height: "100%",
                      "& .MuiDataGrid-cell": {
                        borderRight: "1px solid #ddd",
                        borderBottom: "1px solid #ddd",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        borderRight: "1px solid #ddd",
                        backgroundColor: "#f5f5f5",
                        fontWeight: "bold",
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: "bold",
                      },
                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                      "& .MuiDataGrid-row.totals-row": {
                        backgroundColor: "#f5f5f5 !important",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#e8e8e8 !important",
                        },
                        "& .MuiDataGrid-cell": {
                          cursor: "default",
                          backgroundColor: "#f5f5f5",
                        },
                      },
                    }}
                    rows={transposedRows}
                    columns={transposedColumns}
                    onCellClick={handleCellClick}
                    getRowId={(row) => row.id}
                    sortModel={[
                      {
                        field: "episode",
                        sort: "asc",
                      },
                    ]}
                    sortingOrder={["asc", "desc"]}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => {
                      if (params.row.isTotal) {
                        return "totals-row";
                      }
                      return "";
                    }}
                  />
                </Box>
              )}

              {selectedProduction && categoryItems && !isLoading && transposedRows.length === 0 && (
                <Typography sx={{ textAlign: "center", mt: 4 }}>
                  Δεν βρέθηκαν στοιχεία
                </Typography>
              )}
            </Paper>
          </Box>
        </Grid>
        {setSelectedItem && selectedColumn > 0 && (
          <Drawer
            anchor="right"
            open={Boolean(selectedItem)}
            onClose={handlePanelClose}
            PaperProps={{
              style: { width: "400px" },
            }}
          >
            <ProductionStatusDetails
              item={selectedItem}
              column={selectedColumn}
              details={itemsDetails}
              itemName={selectedItemName}
              budget={selectedBudget}
              threshold={threshold}
              onClose={handlePanelClose}
            />
          </Drawer>
        )}
      </Grid>
    </Box>
  );
};

export default EpisodeGrid;
